import { ExclamationTriangleIcon } from "@heroicons/react/24/outline"
import React from "react"

import { ButtonLink } from "../../../components/shared/Links"

import { authorizeUrl, connectionHealth, isProviderConnectable, providerLabel } from "./selectors"
import { CalendarConnectionSummary, CalendarProvider } from "./types"

interface Props {
  connection: CalendarConnectionSummary
  connectableProviders: CalendarProvider[]
}

// Shown on a card when the connection needs attention. The reconnect CTA is
// only offered when the provider is still connectable in this environment;
// otherwise we show safe guidance without exposing configuration detail.
const HealthReauthBanner = ({ connection, connectableProviders }: Props) => {
  const health = connectionHealth(connection)
  if (health.tone !== "error" && health.tone !== "warning") return null

  // The login-hint keeps the reconnect on the same account: without it the
  // provider silently reuses whichever account is active in the browser.
  const oauthUrl =
    health.needsReauth && isProviderConnectable(connection.provider, connectableProviders)
      ? authorizeUrl(connection.provider, {
          loginHintConnectionPublicId: connection.publicId,
          reconsent: true
        })
      : null

  return (
    <div className="mt-3 flex flex-wrap items-center gap-3 rounded-lg border border-red bg-red/5 p-3">
      <ExclamationTriangleIcon className="size-5 flex-none text-red" aria-hidden="true" />
      <div className="min-w-0 flex-1 text-sm">
        {health.needsReauth ? (
          <span>
            {providerLabel(connection.provider)} needs to be reconnected before it can keep your availability up to
            date.
          </span>
        ) : (
          <span>We&apos;re having trouble syncing this calendar. We&apos;ll keep retrying automatically.</span>
        )}
      </div>
      {oauthUrl && (
        <ButtonLink href={oauthUrl} variant="secondary" className="">
          Reconnect
        </ButtonLink>
      )}
      {health.needsReauth && !oauthUrl && (
        <span className="text-sm text-gray-dark">Contact support to restore this connection.</span>
      )}
    </div>
  )
}

export default HealthReauthBanner
